import { css, StyleSheet } from 'aphrodite';
import { type FC, useMemo, useState } from 'react';

import { Button } from '../../../web/components-v2/Button';
import { ButtonGroup, ButtonGroupSeparator } from '../../../web/components-v2/ButtonGroup';
import { Dropdown, DropdownContent, DropdownItem, DropdownTrigger } from '../../../web/components-v2/Dropdown';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { ChevronDown } from '../../../web/components/svgs/icons/ChevronDown';
import { type CoachMessage, SUGGESTION_STEERS } from '../ai';
import type { CallState } from '../call';
import type { KeywordPin } from '../pins';
import { CallStrip } from './CallStrip';
import { LiveTranscript } from './LiveTranscript';
import { GET_SUGGESTION_SHORTCUT_LABEL, useGetSuggestionShortcut } from './getSuggestionShortcut';
import { PinGrid, type PinTile } from './PinGrid';

/**
 * Props for {@link CenterColumn}. All data is injected from the top-level
 * `FocusedLeadView` container; the column itself holds no server state.
 */
export type CenterColumnProps = {
	callState: CallState;
	muted: boolean;
	onAnswer: () => void;
	onReject: () => void;
	onHangup: () => void;
	onToggleMute: () => void;
	/**
	 * End the active AI voice-roleplay ("Spar") practice session. Forwarded to
	 * CallStrip's `practice`-state End button. No-op when the strip isn't in the
	 * `practice` status.
	 */
	onEndPractice?: () => void;
	onEnableAudio: () => void;
	/**
	 * Send a DTMF digit on the connected call. Forwarded to CallStrip's
	 * inline dialpad popover. Optional so consumers (e.g. tests) can omit
	 * it; the dialpad toggle is hidden when this is not provided.
	 */
	onSendDigit?: (digit: string) => void;
	/**
	 * True while the Twilio SDK is trying to recover from a transient audio
	 * drop on an active call. Forwarded to CallStrip so the connected strip
	 * surfaces an amber "Reconnecting…" indicator until the next
	 * `call:reconnected` (or `call:ended`).
	 */
	reconnecting?: boolean;

	/**
	 * Preferred display for the active caller/callee on the dark CallStrip,
	 * e.g. `"Acme Corp · Patrick Walters"`. Forwarded as `callerDisplay` so
	 * the strip shows the lead identity instead of the raw E.164 number while
	 * dialing/connected/ringing. Falls back to the call's E.164 string when
	 * absent (e.g. before lead data has loaded).
	 */
	peerDisplay?: string;

	/**
	 * The rep's outbound caller-ID (E.164). Forwarded to CallStrip as
	 * `fromNumber` to render the "Calling from …" sub-line on the dialing /
	 * outbound-connected strips.
	 */
	fromNumber?: string;
	/** Forwarded to CallStrip. */
	hideIdleStrip?: boolean;

	/**
	 * Display for the currently RINGING inbound caller, resolved from the
	 * server-side phone-call record + lead contacts. Takes precedence over
	 * `peerDisplay` while the strip is in `ringing` status — the rep needs
	 * to know who is calling them, which may not be the currently-loaded
	 * lead.
	 */
	inboundDisplay?: string;
	/**
	 * Optional click handler that opens the ringing caller's lead in the
	 * focused view without answering. Makes the caller name in the ringing
	 * strip a link so the rep can preview the lead before deciding.
	 */
	onOpenInboundLead?: () => void;

	/** Full chronological message stream. Oldest first — matches `useCoaching`. */
	messages: CoachMessage[];
	/** IDs the user has pinned to the rail. */
	pinnedIds: string[];
	onTogglePin: (id: string) => void;

	/** True while the AI is streaming a reply — drives the hero's streaming dot. */
	isResponding: boolean;

	/** Sticky-bottom conversation starters (parent filters to enabled `trigger='starter'`). */
	starters: KeywordPin[];
	onSendStarter: (pin: KeywordPin) => void;

	/**
	 * Realtime-coaching websocket connection state. When defined, a small
	 * green/red dot is rendered next to the hero eyebrow with a hover tooltip
	 * on the live tile. Omit when coaching is not active.
	 */
	wsConnected?: boolean;

	/**
	 * Shows "Get suggestion" and its steer menu on the live tile during a call, in auto and manual mode
	 * alike. Called with the chosen steer's instruction, or nothing for a plain click.
	 */
	onRequestSuggestion?: (instruction?: string) => void;

	/**
	 * If true, the "Get summary" button is shown above the pinned-card stack.
	 * Set by the parent on `call:ended` and cleared once the user clicks the
	 * button or a new conversation begins.
	 */
	showGetSummaryButton?: boolean;
	onRequestSummary?: () => void;

	/** Server-resolved coaching template name, shown for visibility. Omit when unknown. */
	coachTemplateName?: string;

	/** True until the opening Game Plan reply lands; the row shows only while no tile is on screen. */
	waitingForGamePlan?: boolean;

	/** Show the call transcript under the live suggestion during a call. */
	showLiveTranscript?: boolean;
};

const LIVE_PLACEHOLDER: CoachMessage = { id: 'live-placeholder', role: 'assistant', time: '', text: '' };
const LIVE_TRANSCRIPT_LINES = 30;

/**
 * The middle column: `CallStrip`, an optional action row, the pinned-tile grid, and a one-line row of
 * conversation starters.
 *
 * The grid replaced a vertical stack in which a hero card capped at 40vh and a fixed-height Game Plan
 * left every other pin sharing one short scroll well at the bottom — which reps hit on every call,
 * since three pins arrive automatically before anyone touches anything. The latest AI message is now
 * a tile in that grid flagged LIVE rather than a separate hero, and the starters collapsed from a row
 * each to a single chip line; both changes exist to give the tiles their height back.
 *
 * `PinGrid` owns the layout shape. This component owns which messages become tiles and in what order.
 */
export const CenterColumn: FC<CenterColumnProps> = ({
	callState,
	muted,
	onAnswer,
	onReject,
	onHangup,
	onToggleMute,
	onEndPractice,
	onEnableAudio,
	onSendDigit,
	reconnecting,
	peerDisplay,
	fromNumber,
	hideIdleStrip,
	inboundDisplay,
	onOpenInboundLead,
	messages,
	pinnedIds,
	onTogglePin,
	isResponding,
	starters,
	onSendStarter,
	wsConnected,
	onRequestSuggestion,
	showGetSummaryButton,
	onRequestSummary,
	coachTemplateName,
	waitingForGamePlan,
	showLiveTranscript,
}) => {
	// Which demoted pins the rep pulled back into the grid, newest first. Kept local to the column: it
	// is a view preference for this conversation, not something worth persisting.
	const [promotedIds, setPromotedIds] = useState<string[]>([]);

	// Tiles the rep closed. The live slot must not fall back to one of these, or closing a tile just moves it.
	const [dismissedIds, setDismissedIds] = useState<string[]>([]);
	// A Spar practice call streams a transcript and coaching just like a real one, so it gets the same live box.
	const inCall = callState.status === 'connected' || callState.status === 'practice';

	const tiles = useMemo<PinTile[]>(() => {
		const pinned = messages.filter(m => pinnedIds.includes(m.id));
		const gamePlan = pinned.find(m => m.pinnedKeyword === 'GAME PLAN');

		// Promoted pins jump ahead of the rest so a chip click actually brings the tile on screen.
		const others = pinned
			.filter(m => m !== gamePlan)
			.sort((a, b) => promotedIds.indexOf(b.id) - promotedIds.indexOf(a.id));

		// Suggestions are the untagged replies; a keyword pin's reply (and the Game Plan) is a pin even
		// once the rep unpins it. Only the newest can be live, so closing it doesn't surface an older one.
		const suggestions = messages.filter(m => m.role === 'assistant' && !m.pinnedKeyword && !pinnedIds.includes(m.id));
		const newest = suggestions[suggestions.length - 1];
		const live = newest && !dismissedIds.includes(newest.id) ? newest : undefined;
		const liveTile: PinTile[] = live
			? [
					{
						msg: live,
						kind: 'live',
						feed: suggestions
							.slice(0, -1)
							.filter(m => !dismissedIds.includes(m.id))
							.reverse(),
					},
				]
			: inCall
				? [{ msg: LIVE_PLACEHOLDER, kind: 'live', feed: [], placeholder: true }]
				: [];
		const pinTiles: PinTile[] = [
			...(gamePlan ? [{ msg: gamePlan, kind: 'lead' as const }] : []),
			...others.map(msg => ({ msg, kind: 'pin' as const })),
		];

		// During a call the live suggestion goes first so PinGrid gives it the fixed column beside the pins, and
		// the rep never hunts for it. Before and after the call the Game Plan leads and live sorts last.
		return inCall ? [...liveTile, ...pinTiles] : [...pinTiles, ...liveTile];
	}, [messages, pinnedIds, promotedIds, dismissedIds, inCall]);

	const dismissTile = (id: string) => {
		setDismissedIds(prev => (prev.includes(id) ? prev : [...prev, id]));
		if (pinnedIds.includes(id)) onTogglePin(id);
	};

	const showSuggestionButton = Boolean(onRequestSuggestion && inCall);
	const showSummaryButton = Boolean(showGetSummaryButton && onRequestSummary);

	// The button and the keyboard shortcut share this, so the shortcut is only live when the button is.
	const requestSuggestion = (instruction?: string) => {
		if (!showSuggestionButton || isResponding) return;
		onRequestSuggestion?.(instruction);
	};
	useGetSuggestionShortcut(() => requestSuggestion());

	// "Get suggestion" and the transcript live inside the live tile, so auto and manual modes share one layout
	// and the rep can see the prospect's last line land before asking for a suggestion.
	const transcriptLines = showLiveTranscript
		? messages.filter(m => m.role === 'transcript').slice(-LIVE_TRANSCRIPT_LINES)
		: [];
	const gridTiles = inCall
		? tiles.map(tile =>
				tile.kind === 'live'
					? {
							...tile,
							actions: showSuggestionButton ? (
								<ButtonGroup>
									<Button
										variant='primary'
										size='sm'
										onClick={() => requestSuggestion()}
										disabled={isResponding}
										title={`Get suggestion (${GET_SUGGESTION_SHORTCUT_LABEL})`}
										aria-keyshortcuts={GET_SUGGESTION_SHORTCUT_LABEL}
										data-testid='get-suggestion-button'
									>
										{isResponding ? 'Analyzing…' : 'Get suggestion'}
									</Button>
									<ButtonGroupSeparator />
									<Dropdown>
										<DropdownTrigger
											disabled={isResponding}
											render={
												<Button
													variant='primary'
													size='sm'
													aria-label='More suggestion options'
													data-testid='get-suggestion-options'
												/>
											}
										>
											<ChevronDown width={16} height={16} />
										</DropdownTrigger>
										<DropdownContent align='end' styles={[styles.steerMenu]}>
											{SUGGESTION_STEERS.map(steer => (
												<DropdownItem key={steer.id} onClick={() => requestSuggestion(steer.instruction)}>
													{steer.label}
												</DropdownItem>
											))}
										</DropdownContent>
									</Dropdown>
								</ButtonGroup>
							) : undefined,
							footer: showLiveTranscript ? <LiveTranscript lines={transcriptLines} /> : undefined,
						}
					: tile
			)
		: tiles;

	return (
		<div className={css(styles.col)}>
			<CallStrip
				callState={callState}
				muted={muted}
				onAnswer={onAnswer}
				onReject={onReject}
				onHangup={onHangup}
				onToggleMute={onToggleMute}
				onEndPractice={onEndPractice}
				onEnableAudio={onEnableAudio}
				callerDisplay={peerDisplay}
				fromNumber={fromNumber}
				hideIdleStrip={hideIdleStrip}
				inboundDisplay={inboundDisplay}
				onOpenInboundLead={onOpenInboundLead}
				onSendDigit={onSendDigit}
				reconnecting={reconnecting}
			/>

			{showSummaryButton && (
				<div className={css(styles.actionRow)}>
					<Button
						variant='outline'
						size='sm'
						onClick={onRequestSummary}
						disabled={isResponding}
						data-testid='get-summary-button'
					>
						{isResponding ? 'Generating summary…' : 'Get summary'}
					</Button>
				</div>
			)}

			{waitingForGamePlan && tiles.length === 0 && (
				<div className={css(styles.waitingRow)} role='status' data-testid='waiting-for-game-plan'>
					<LoadingSpinner type='small' />
					<span>Waiting for game plan to load...</span>
				</div>
			)}

			<PinGrid
				tiles={gridTiles}
				liveAside={inCall}
				onUnpin={dismissTile}
				onPromote={id => setPromotedIds(prev => [...prev.filter(existing => existing !== id), id])}
				isResponding={isResponding}
				wsConnected={wsConnected}
			/>

			{(starters.length > 0 || coachTemplateName) && (
				<div className={css(styles.starterFooter)}>
					{starters.length > 0 && <span className={css(styles.starterLabel)}>ADD</span>}
					{starters.map(p => (
						<button
							key={p.id}
							type='button'
							className={css(styles.starterChip)}
							onClick={() => onSendStarter(p)}
							title={`Send: ${p.label}`}
						>
							<span className={css(styles.starterDot)} style={{ background: p.color }} />
							{p.label}
						</button>
					))}
					{coachTemplateName && (
						<span className={css(styles.coachTemplate)} title={coachTemplateName}>
							Coach: {coachTemplateName}
						</span>
					)}
				</div>
			)}
		</div>
	);
};

const styles = StyleSheet.create({
	// The popup defaults to the anchor's width, and the anchor here is only the caret.
	steerMenu: {
		width: 'max-content',
		maxWidth: 'var(--available-width)',
		whiteSpace: 'nowrap',
	},
	col: {
		display: 'flex',
		flexDirection: 'column',
		minWidth: 0,
		minHeight: 0,
		flex: 1,
		background: 'var(--bg)',
	},
	waitingRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 8,
		padding: '12px 20px 0',
		flexShrink: 0,
		fontSize: 12,
		color: 'var(--ink-faint)',
	},
	actionRow: {
		display: 'flex',
		gap: 8,
		padding: '10px 20px 0',
		flexShrink: 0,
	},
	// One line of chips rather than a row per starter — the old footer cost about 90px of tile height.
	starterFooter: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 8,
		padding: '9px 20px 12px',
		borderTop: '1px solid var(--border)',
		background: 'var(--bg)',
	},
	starterLabel: {
		fontFamily: 'var(--font-mono)',
		fontSize: 9.5,
		fontWeight: 600,
		letterSpacing: '0.1em',
		color: 'var(--ink-faint)',
	},
	starterChip: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		fontSize: 11,
		padding: '4px 10px',
		borderRadius: 999,
		border: '1px solid var(--border)',
		background: 'var(--bg-elev)',
		color: 'var(--ink)',
		cursor: 'pointer',
		whiteSpace: 'nowrap',
		':hover': {
			background: 'var(--hover)',
		},
	},
	coachTemplate: {
		marginLeft: 'auto',
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		fontFamily: 'var(--font-mono)',
		fontSize: 10,
		color: 'var(--ink-faint)',
	},
	starterDot: {
		width: 6,
		height: 6,
		borderRadius: '50%',
		flexShrink: 0,
	},
});
